// frontend/src/pages/Facturation.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatEuros, formatEurosShort, formatDateFr, formatDuration } from '@/lib/format';
import {
  Clock, Receipt, Send, CheckCircle2, AlertCircle, FileText,
  Download, Plus, Filter, BarChart2, TrendingUp, Users, Euro, Bell,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('lawliet_token')}` };
}

const STATUS = {
  draft:   { label: 'Brouillon', color: 'bg-slate-100 text-slate-600',    icon: FileText },
  sent:    { label: 'Envoyée',   color: 'bg-blue-100 text-blue-700',      icon: Send },
  paid:    { label: 'Payée',     color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-700',        icon: AlertCircle },
};

const SkeletonRow = () => (
  <tr className="border-b border-slate-100">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 animate-pulse rounded bg-slate-100" />
      </td>
    ))}
  </tr>
);

function GenerateInvoiceModal({ selectedEntries, clients, onClose, onSuccess }) {
  const [clientId, setClientId] = useState('');
  const [dueDays, setDueDays] = useState(30);
  const [tvaRate, setTvaRate] = useState(0.20);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ids = [...new Set(selectedEntries.map((e) => e.client_id).filter(Boolean))];
    if (ids.length === 1) setClientId(ids[0]);
  }, [selectedEntries]);

  const subtotal = selectedEntries.reduce((s, e) => s + e.amount_euros, 0);
  const tva = subtotal * tvaRate;
  const total = subtotal + tva;

  const handleGenerate = async () => {
    if (!clientId) { toast.error('Sélectionnez un client'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/invoices`,
        {
          client_id: clientId,
          time_entry_ids: selectedEntries.map((e) => e.id),
          due_days: Number(dueDays),
          tva_rate: Number(tvaRate),
        },
        { headers: authHeaders() }
      );
      toast.success('Facture générée avec succès');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="mb-4 text-lg font-semibold text-slate-900">Générer une facture</h3>

        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm text-slate-600">{selectedEntries.length} saisie(s) sélectionnée(s)</p>
          <div className="mt-1 flex gap-4 text-sm font-medium">
            <span>Sous-total HT : {formatEuros(subtotal)}</span>
            <span>TVA {Math.round(tvaRate * 100)}% : {formatEuros(tva)}</span>
            <span className="text-slate-900">Total TTC : {formatEuros(total)}</span>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="inv-client" className="mb-1 block text-sm">Client *</Label>
            <select
              id="inv-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner un client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="inv-tva" className="mb-1 block text-sm">TVA (%)</Label>
              <Input
                id="inv-tva"
                type="number"
                min="0"
                max="100"
                value={Math.round(tvaRate * 100)}
                onChange={(e) => setTvaRate(Number(e.target.value) / 100)}
              />
            </div>
            <div>
              <Label htmlFor="inv-due" className="mb-1 block text-sm">Délai paiement (jours)</Label>
              <Input
                id="inv-due"
                type="number"
                min="0"
                value={dueDays}
                onChange={(e) => setDueDays(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={handleGenerate} disabled={saving}>
            {saving ? 'Génération…' : 'Générer la facture'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

function computeAnalytics(allInvoices) {
  const paid = allInvoices.filter((i) => i.status === 'paid');
  const sent = allInvoices.filter((i) => i.status === 'sent');
  const overdue = allInvoices.filter((i) => i.status === 'overdue');

  const sum = (arr, key = 'total_euros') => arr.reduce((s, i) => s + (i[key] || 0), 0);

  const totalBilled = sum(allInvoices);
  const totalPaid = sum(paid);
  const totalPending = sum(sent);
  const totalOverdue = sum(overdue);
  const recoverable = totalPaid + totalPending + totalOverdue;
  const recoveryRate = recoverable > 0 ? Math.round((totalPaid / recoverable) * 100) : 0;

  // Last 12 months revenue (paid only)
  const now = new Date();
  const monthData = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    return { month: `${MONTHS_FR[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`, year: d.getFullYear(), m: d.getMonth() + 1, value: 0 };
  });
  paid.forEach((inv) => {
    const d = new Date(inv.issue_date);
    const slot = monthData.find((s) => s.year === d.getFullYear() && s.m === d.getMonth() + 1);
    if (slot) slot.value += inv.total_euros || 0;
  });

  // Top clients by revenue (paid)
  const byClient = {};
  paid.forEach((inv) => {
    if (!byClient[inv.client_name]) byClient[inv.client_name] = 0;
    byClient[inv.client_name] += inv.total_euros || 0;
  });
  const topClients = Object.entries(byClient)
    .map(([name, value]) => ({ name: name.length > 22 ? name.slice(0, 20) + '…' : name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  return { totalBilled, totalPaid, totalPending, totalOverdue, recoveryRate, monthData, topClients };
}

function KpiCard({ icon: Icon, label, value, color = 'text-slate-900', sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
        <Icon size={16} className="text-slate-300" />
      </div>
      <p className={`text-2xl font-semibold ${color}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function Facturation({ user, cabinet, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showGenModal, setShowGenModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [activeTab, setActiveTab] = useState('saisies');

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const params = new URLSearchParams({ status: 'draft' });
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      const { data } = await axios.get(`${API}/time-entries?${params}`, { headers: authHeaders() });
      setEntries(data.entries || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des saisies');
    } finally {
      setLoadingEntries(false);
    }
  }, [filterDateFrom, filterDateTo]);

  const loadInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await axios.get(`${API}/invoices?${params}`, { headers: authHeaders() });
      setInvoices(data.invoices || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoadingInvoices(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    axios.get(`${API}/clients`, { headers: authHeaders() }).then(({ data }) => setClients(data.clients || []));
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await axios.get(`${API}/invoices`, { headers: authHeaders() });
      setAnalytics(computeAnalytics(data.invoices || []));
    } catch (e) {
      toast.error('Erreur lors du chargement des analytiques');
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'analytiques') loadAnalytics();
  }, [activeTab, loadAnalytics]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedEntries = entries.filter((e) => selectedIds.has(e.id));
  const selectedTotal = selectedEntries.reduce((s, e) => s + e.amount_euros, 0);

  const handleDownloadPdf = (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('lawliet_token');
    fetch(`${API}/invoices/${invoiceId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `${invoiceNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      const { data } = await axios.post(`${API}/invoices/${invoiceId}/send`, {}, { headers: authHeaders() });
      toast.success(`Facture envoyée à ${data.sent_to}`);
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}/mark-paid`, {}, { headers: authHeaders() });
      toast.success('Facture marquée comme payée');
      loadInvoices();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      const { data } = await axios.post(`${API}/invoices/${invoiceId}/remind`, {}, { headers: authHeaders() });
      toast.success(`Rappel envoyé à ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi du rappel");
    }
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'EB Garamond, serif' }}>
            Facturation
          </h1>
          <p className="text-sm text-slate-500 mt-1">Suivi des heures et gestion des factures client</p>
        </div>

        <Tabs defaultValue="saisies" onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="saisies" className="flex items-center gap-2">
              <Clock size={14} /> Saisies de temps
            </TabsTrigger>
            <TabsTrigger value="factures" className="flex items-center gap-2">
              <Receipt size={14} /> Factures
            </TabsTrigger>
            <TabsTrigger value="analytiques" className="flex items-center gap-2">
              <BarChart2 size={14} /> Analytiques
            </TabsTrigger>
          </TabsList>

          <TabsContent value="saisies">
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <Filter size={14} className="text-slate-400" />
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-400">→</span>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          setSelectedIds(e.target.checked ? new Set(entries.map((en) => en.id)) : new Set())
                        }
                        checked={selectedIds.size === entries.length && entries.length > 0}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Dossier</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Description</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Durée</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Montant HT</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingEntries ? (
                    [1, 2, 3].map((i) => <SkeletonRow key={i} />)
                  ) : entries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center text-slate-400">
                        <Clock className="mx-auto mb-2 h-8 w-8 text-slate-200" />
                        <p className="font-medium">Aucune saisie non facturée</p>
                        <p className="text-xs mt-1">Les saisies apparaîtront ici dès qu'elles seront créées depuis un dossier.</p>
                      </td>
                    </tr>
                  ) : (
                    entries.map((entry) => (
                      <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(entry.id)}
                            onChange={() => toggleSelect(entry.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-slate-600">{formatDateFr(entry.date)}</td>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-xs truncate">{entry.dossier_title}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{entry.description}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{formatDuration(entry.duration_minutes)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatEuros(entry.amount_euros)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {selectedIds.size > 0 && (
              <div className="fixed bottom-0 inset-x-0 border-t border-slate-200 bg-white px-6 py-3 flex items-center justify-between shadow-lg z-40">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{selectedIds.size}</span> saisie(s) ·{' '}
                  <span className="font-semibold text-slate-900">{formatEuros(selectedTotal)} HT</span>
                </p>
                <Button onClick={() => setShowGenModal(true)} className="flex items-center gap-2">
                  <Plus size={14} /> Générer une facture
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="factures">
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              {['', 'draft', 'sent', 'paid', 'overdue'].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    filterStatus === s
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {s === '' ? 'Toutes' : STATUS[s]?.label || s}
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">N° Facture</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Client</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Émission</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Échéance</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Total TTC</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600">Statut</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingInvoices ? (
                    [1, 2, 3].map((i) => <SkeletonRow key={i} />)
                  ) : invoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center text-slate-400">
                        <Receipt className="mx-auto mb-2 h-8 w-8 text-slate-200" />
                        <p className="font-medium">Aucune facture</p>
                        <p className="text-xs mt-1">Sélectionnez des saisies de temps pour générer votre première facture.</p>
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv) => {
                      const s = STATUS[inv.status] || STATUS.draft;
                      const StatusIcon = s.icon;
                      return (
                        <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-slate-800">{inv.invoice_number}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{inv.client_name}</td>
                          <td className="px-4 py-3 text-slate-600">{formatDateFr(inv.issue_date)}</td>
                          <td className="px-4 py-3 text-slate-600">{formatDateFr(inv.due_date)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatEuros(inv.total_euros)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.color}`}>
                              <StatusIcon size={10} />
                              {s.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                title="Télécharger PDF"
                              >
                                <Download size={14} />
                              </button>
                              {inv.status === 'draft' && (
                                <button
                                  onClick={() => handleSendInvoice(inv.id)}
                                  className="rounded p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                                  title="Envoyer par email"
                                >
                                  <Send size={14} />
                                </button>
                              )}
                              {(inv.status === 'sent' || inv.status === 'overdue') && (
                                <>
                                  <button
                                    onClick={() => handleSendReminder(inv.id)}
                                    className="rounded p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-600"
                                    title="Envoyer un rappel de paiement"
                                  >
                                    <Bell size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleMarkPaid(inv.id)}
                                    className="rounded p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                                    title="Marquer comme payée"
                                  >
                                    <CheckCircle2 size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="analytiques">
            {loadingAnalytics ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-28 animate-pulse rounded-xl border border-slate-200 bg-slate-50" />
                ))}
              </div>
            ) : !analytics ? null : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
                  <KpiCard
                    icon={Euro}
                    label="CA facturé"
                    value={`${formatEurosShort(analytics.totalBilled)}`}
                    sub="Total TTC émis"
                  />
                  <KpiCard
                    icon={CheckCircle2}
                    label="CA encaissé"
                    value={`${formatEurosShort(analytics.totalPaid)}`}
                    color="text-emerald-600"
                    sub="Factures payées"
                  />
                  <KpiCard
                    icon={Send}
                    label="En attente"
                    value={`${formatEurosShort(analytics.totalPending)}`}
                    color="text-blue-600"
                    sub="Factures envoyées"
                  />
                  <KpiCard
                    icon={TrendingUp}
                    label="Taux de recouvrement"
                    value={`${analytics.recoveryRate} %`}
                    color={analytics.recoveryRate >= 80 ? 'text-emerald-600' : analytics.recoveryRate >= 50 ? 'text-amber-600' : 'text-red-600'}
                    sub={`${formatEurosShort(analytics.totalOverdue)} en retard`}
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                  <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-5">
                    <h3 className="mb-4 text-sm font-semibold text-slate-700">CA encaissé par mois (12 derniers mois)</h3>
                    {analytics.monthData.some((m) => m.value > 0) ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={analytics.monthData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}€`} />
                          <Tooltip
                            formatter={(v) => [formatEuros(v), 'CA encaissé']}
                            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                          />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {analytics.monthData.map((_, i) => (
                              <Cell key={i} fill={i === analytics.monthData.length - 1 ? '#2563eb' : '#bfdbfe'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-[220px] flex-col items-center justify-center text-center">
                        <BarChart2 className="mb-2 h-8 w-8 text-slate-200" />
                        <p className="text-sm font-medium text-slate-500">Aucun encaissement pour l'instant</p>
                        <p className="mt-1 text-xs text-slate-400">Le CA encaissé s'affichera ici dès qu'une facture sera payée.</p>
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
                    <h3 className="mb-4 text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      <Users size={13} className="text-slate-400" /> Top clients (CA encaissé)
                    </h3>
                    {analytics.topClients.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center pt-8">Aucune facture payée</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={analytics.topClients} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}€`} />
                          <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: '#64748b' }} />
                          <Tooltip
                            formatter={(v) => [formatEuros(v), 'CA encaissé']}
                            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                          />
                          <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {showGenModal && (
        <GenerateInvoiceModal
          selectedEntries={selectedEntries}
          clients={clients}
          onClose={() => setShowGenModal(false)}
          onSuccess={() => {
            setShowGenModal(false);
            setSelectedIds(new Set());
            loadEntries();
          }}
        />
      )}
    </Layout>
  );
}
